"use client";

import Image from "@/components/OptimizedImage";

interface HeroImageData {
  src: string;
  alt: string;
}

interface AboutHeroProps {
  title?: string;
  locationInfo?: string;
  aboutText?: string;
  images?: HeroImageData[];
}

export function AboutHero({ title, locationInfo, aboutText, images }: AboutHeroProps) {
  // Fallback images if frontmatter is empty to prevent layout collapse
  const displayImages = images || [];

  return (
    <>
      <div className="pt-16 md:pt-20" aria-hidden="true" />
      <section className="hero-padding container">
        {/* Title & Location */}
        <div className="space-y-6 mb-8 lg:mb-10">
          <h1 className="text-3xl tracking-tight">
            {title || "About me"}
          </h1>
          {locationInfo && (
            <p className="max-w-xl text-xl leading-relaxed text-muted-foreground uppercase tracking-widest font-light whitespace-pre-line">
              {locationInfo}
            </p>
          )}
        </div>

        {/* Text + Photos side by side */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:gap-10">
          {/* Text column */}
          <div className="lg:w-[52%] mb-8 lg:mb-0">
            {aboutText && (
              <p className="text-lg leading-relaxed text-muted-foreground whitespace-pre-line">
                {aboutText}
              </p>
            )}
          </div>

          {/* Photo grid — ~20% smaller */}
          <div className="grid grid-cols-12 grid-rows-6 gap-3 w-full lg:w-[48%] h-[450px] md:h-[550px]">
            {/* Main Portrait Image (Index 0) */}
            {displayImages[0] && (
              <div className="col-span-7 row-span-6 relative overflow-hidden rounded-xl">
                <Image
                  src={displayImages[0].src}
                  alt={displayImages[0].alt}
                  fill
                  sizes="(max-width: 1024px) 100vw, 35vw"
                  className="object-cover transition-transform duration-500 hover:scale-105"
                  priority
                />
              </div>
            )}

            {/* Top Secondary Image (Index 1) */}
            {displayImages[1] && (
              <div className="col-span-5 row-span-3 relative overflow-hidden rounded-xl">
                <Image
                  src={displayImages[1].src}
                  alt={displayImages[1].alt}
                  fill
                  sizes="(max-width: 1024px) 100vw, 25vw"
                  className="object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>
            )}

            {/* Bottom Secondary Image (Index 2) */}
            {displayImages[2] && (
              <div className="col-span-5 row-span-3 relative overflow-hidden rounded-xl">
                <Image
                  src={displayImages[2].src}
                  alt={displayImages[2].alt}
                  fill
                  sizes="(max-width: 1024px) 100vw, 25vw"
                  className="object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
